import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink, Pencil, Loader2, CalendarIcon, ChevronDown } from "lucide-react";
import { format, parseISO, getDay } from "date-fns";
import CustomerEditModal, { Customer } from "./CustomerEditModal";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";

interface Listing {
  listing_id: string;
  name: string;
  price: number;
  business_id?: string;
}

interface ListingSchedule {
  weekly: Record<string, string[]>;
  exceptions?: string[];
}

interface Booking {
  booking_id: string;
  short_id?: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  booking_date: string;
  booking_time: string;
  status: string;
  notes: string | null;
  created_at?: string;
  listing_id: string | null;
  listings?: {
    name: string;
    price: number;
    business_id: string;
  } | null;
}

interface BookingDetailsModalProps {
  booking: Booking | null;
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  businessId: string | null;
  readOnly?: boolean;
}

// Map JS getDay() (0=Sun) to schedule keys
const DAY_INDEX_TO_KEY: Record<number, string> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
};

// Convert "HH:mm" (24h) to "h:mm AM/PM" (12h)
const format24hTo12h = (time24: string): string => {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr) || 0;
  const m = mStr || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
};

// Convert "h:mm AM/PM" (12h) to "HH:mm:00" (24h) for DB storage
const convert12hTo24h = (time12h: string): string => {
  if (!time12h) return "00:00:00";
  const [time, modifier] = time12h.split(" ");
  const parts = time.split(":");
  let hours = parts[0];
  const minutes = parts[1];
  if (hours === "12") hours = "00";
  if (modifier === "PM") hours = String(parseInt(hours, 10) + 12);
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:00`;
};

const BookingDetailsModal = ({ booking, isOpen, onClose, onSaveSuccess, businessId, readOnly = false }: BookingDetailsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [bookingDate, setBookingDate] = useState<string>("");
  const [bookingTime, setBookingTime] = useState<string>("");
  const [price, setPrice] = useState<number>(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [displayCustomerName, setDisplayCustomerName] = useState("");
  const [displayCustomerEmail, setDisplayCustomerEmail] = useState("");

  // Schedule data for the listing
  const [schedule, setSchedule] = useState<ListingSchedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [maxCapacity, setMaxCapacity] = useState<number>(1);
  // Map of "HH:mm" -> count of confirmed bookings for the selected date
  const [slotBookingCounts, setSlotBookingCounts] = useState<Record<string, number>>({});

  const calendarRef = useRef<HTMLDivElement>(null);

  // Close calendar if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCalendarOpen]);

  // Fetch the listing's schedule and capacity when modal opens
  const fetchSchedule = useCallback(async (listingId: string) => {
    setScheduleLoading(true);
    const { data, error } = await supabase
      .from("listings")
      .select("schedule, spots")
      .eq("listing_id", listingId)
      .maybeSingle();

    if (!error && data) {
      if (data.schedule) {
        setSchedule(data.schedule as unknown as ListingSchedule);
      } else {
        setSchedule(null);
      }
      setMaxCapacity(data.spots || 1);
    } else {
      setSchedule(null);
      setMaxCapacity(1);
    }
    setScheduleLoading(false);
  }, []);

  // Fetch booking pax counts for a specific date + listing (matches public page logic)
  const fetchBookingCounts = useCallback(async (listingId: string, dateStr: string) => {
    const { data, error } = await (supabase as any).rpc("get_slot_usage", {
      p_listing_id: listingId,
      p_dates: [dateStr],
      // Don't count the current booking being edited
      p_exclude_booking: booking?.booking_id ?? null,
    });

    if (!error && data) {
      const counts: Record<string, number> = {};
      (data as any[]).forEach((b) => {
        const pax = b.number_of_pax || 1;
        // Store under both the raw time key AND the normalized 24h key
        // so we can match regardless of format stored in DB
        const rawTime = b.booking_time;
        const time24 = convert12hTo24h(rawTime).slice(0, 5);
        counts[time24] = (counts[time24] || 0) + pax;
        if (rawTime !== time24) {
          counts[rawTime] = (counts[rawTime] || 0) + pax;
        }
      });
      setSlotBookingCounts(counts);
    } else {
      setSlotBookingCounts({});
    }
  }, [booking?.booking_id]);

  useEffect(() => {
    if (isOpen && booking?.listing_id) {
      fetchSchedule(booking.listing_id);
    }
  }, [isOpen, booking?.listing_id, fetchSchedule]);

  // Refetch booking counts whenever the date changes
  useEffect(() => {
    if (isOpen && booking?.listing_id && bookingDate) {
      fetchBookingCounts(booking.listing_id, bookingDate);
    }
  }, [isOpen, booking?.listing_id, bookingDate, bookingTime, fetchBookingCounts]);

  useEffect(() => {
    if (booking) {
      setStatus(booking.status || "");
      setBookingDate(booking.booking_date || "");
      setBookingTime(booking.booking_time || "");
      setPrice(booking.listings?.price || 0);
      setDisplayCustomerName(booking.customer_name || "");
      setDisplayCustomerEmail(booking.customer_email || "");
    }
  }, [booking]);

  // Get available time slots for a given date based on the schedule
  const getTimeSlotsForDate = useCallback((dateStr: string): string[] => {
    if (!schedule?.weekly || !dateStr) return [];
    try {
      const date = parseISO(dateStr);
      const dayIndex = getDay(date);
      const dayKey = DAY_INDEX_TO_KEY[dayIndex];
      const slots = schedule.weekly[dayKey] || [];

      // Check exceptions
      if (schedule.exceptions?.includes(dateStr)) return [];

      return slots;
    } catch {
      return [];
    }
  }, [schedule]);

  // Get available slots for currently selected date, filtering out sold-out and past ones
  const allSlotsForDate = getTimeSlotsForDate(bookingDate);
  const availableSlots = allSlotsForDate.filter((slot24) => {
    // Filter out past time slots if the selected date is today
    const now = new Date();
    const todayStr = format(now, "yyyy-MM-dd");
    if (bookingDate === todayStr) {
      const [h, m] = slot24.split(":").map(Number);
      const slotMinutes = h * 60 + m;
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      if (slotMinutes <= currentMinutes) return false;
    }

    // Check pax count under both the raw 24h key and the 12h formatted key
    const slot12 = format24hTo12h(slot24);
    const count = Math.max(slotBookingCounts[slot24] || 0, slotBookingCounts[slot12] || 0);
    // Always keep the current booking's own time slot visible
    const currentSlot24 = convert12hTo24h(booking?.booking_time || "").slice(0, 5);
    if (slot24 === currentSlot24 && bookingDate === booking?.booking_date) return true;
    return count < maxCapacity;
  });

  // Check if a calendar day should be disabled (no slots or past date)
  const isDateDisabled = useCallback((date: Date): boolean => {
    // Disable past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;

    if (!schedule?.weekly) return false;
    const dayIndex = getDay(date);
    const dayKey = DAY_INDEX_TO_KEY[dayIndex];
    const slots = schedule.weekly[dayKey] || [];
    if (slots.length === 0) return true;

    // Check exceptions
    const dateStr = format(date, "yyyy-MM-dd");
    if (schedule.exceptions?.includes(dateStr)) return true;

    return false;
  }, [schedule]);

  // When date changes, reset time if current time is not available on new date
  useEffect(() => {
    if (!bookingDate || !schedule) return;
    const slots = getTimeSlotsForDate(bookingDate);
    if (slots.length === 0) {
      setBookingTime("");
      return;
    }
    // Check if current bookingTime (in 12h) matches any slot (in 24h)
    const current24 = convert12hTo24h(bookingTime).slice(0, 5);
    if (!slots.includes(current24)) {
      // Auto-select the first available slot
      setBookingTime(format24hTo12h(slots[0]));
    }
  }, [bookingDate, schedule, getTimeSlotsForDate]);

  const handleSave = async () => {
    if (!booking) return;

    setLoading(true);

    const { error } = await supabase
      .from("bookings")
      .update({
        status: status,
        booking_date: bookingDate,
        booking_time: bookingTime,
      })
      .eq("booking_id", booking.booking_id);

    setLoading(false);
    if (error) {
      toast.error("Failed to update booking");
    } else {
      toast.success("Booking updated");
      onSaveSuccess();
      onClose();
    }
  };

  const handleEditCustomer = async () => {
    if (!booking) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("email", booking.customer_email)
      .single();

    setLoading(false);
    if (error) {
      toast.error("Customer not found in database");
    } else {
      setCustomerData(data);
      setIsCustomerModalOpen(true);
    }
  };

  const handleDelete = async () => {
    if (!booking) return;
    setLoading(true);
    const { error } = await supabase
      .from("bookings")
      .delete()
      .eq("booking_id", booking.booking_id);

    setLoading(false);
    if (error) {
      toast.error("Failed to delete booking");
    } else {
      toast.success("Booking deleted");
      onSaveSuccess();
      onClose();
    }
  };

  if (!booking) return null;

  const openPublicListing = () => {
    const protocol = window.location.protocol;
    const host = window.location.hostname.replace("admin.", "");
    const port = window.location.port ? `:${window.location.port}` : "";
    window.open(`${protocol}//${host}${port}/listing/${booking.listing_id}`, "_blank");
  };
  const formatStatusLabel = (value: string) => value.replace(/_/g, " ");
  const statusDotClass = cn(
    "h-2 w-2 shrink-0 rounded-full",
    status === "confirmed" && "bg-green-500",
    status === "completed" && "bg-muted-foreground/40",
    status === "no_show" && "bg-amber-500",
    status === "cancelled" && "bg-red-500",
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="admin-modal-content">
          <DialogHeader className="admin-modal-header">
            <DialogTitle className="font-heading text-left">
              {readOnly ? "Booking details" : "Edit booking"}
            </DialogTitle>
            <DialogDescription className="text-left">
              Booking ID: {booking.short_id || booking.booking_id?.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>

          {readOnly ? (
            <dl className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card text-sm">
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-muted-foreground">Listing</dt>
                <dd className="flex min-w-0 items-center gap-2 font-medium">
                  <span className="truncate">{booking.listings?.name || "Unknown listing"}</span>
                  <button
                    type="button"
                    onClick={openPublicListing}
                    className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                    title="View public listing page"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-muted-foreground">Date</dt>
                <dd className="font-medium">{bookingDate ? format(parseISO(bookingDate), "d MMM yyyy") : "—"}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-muted-foreground">Time slot</dt>
                <dd className="font-medium">{bookingTime || "—"}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-muted-foreground">Status</dt>
                <dd className="flex items-center gap-2 font-medium">
                  <span className={statusDotClass} />
                  <span className="capitalize">{formatStatusLabel(status)}</span>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-muted-foreground">Customer</dt>
                <dd className="min-w-0 text-right">
                  <div className="truncate font-medium">{displayCustomerName}</div>
                  <div className="truncate text-xs text-muted-foreground">{displayCustomerEmail}</div>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-muted-foreground">Price</dt>
                <dd className="font-medium tabular-nums">${price.toFixed(2)}</dd>
              </div>
            </dl>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Listing</Label>
                <div className="flex h-9 w-full items-center gap-3 rounded-md border border-input bg-card px-3 text-sm">
                  <span className="flex-1 truncate font-medium">{booking.listings?.name || "Unknown listing"}</span>
                  <button
                    type="button"
                    onClick={openPublicListing}
                    className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                    title="View public listing page"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Date</Label>
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-9 w-full justify-between bg-card px-3 font-medium hover:bg-muted/40"
                      >
                        <span className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                          {bookingDate ? format(parseISO(bookingDate), "d MMM yyyy") : "Select date"}
                        </span>
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto overflow-hidden p-0 shadow-lg">
                      <Calendar
                        mode="single"
                        selected={bookingDate ? parseISO(bookingDate) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            setBookingDate(format(date, "yyyy-MM-dd"));
                            setIsCalendarOpen(false);
                          }
                        }}
                        disabled={isDateDisabled}
                        showOutsideDays={false}
                        className="p-3"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1.5">
                  <Label>Time slot</Label>
                  {scheduleLoading ? (
                    <div className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-card px-3 text-sm">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Loading slots...</span>
                    </div>
                  ) : (
                    <Select value={bookingTime} onValueChange={setBookingTime} disabled={availableSlots.length === 0}>
                      <SelectTrigger className="h-9 w-full bg-card">
                        <SelectValue placeholder={availableSlots.length === 0 ? "No slots available" : "Choose a time slot"} />
                      </SelectTrigger>
                      <SelectContent position="popper" hideScrollButtons className="max-h-[200px] w-[var(--radix-select-trigger-width)] overflow-y-auto">
                        {availableSlots.map((slot24) => {
                          const slot12 = format24hTo12h(slot24);
                          return (
                            <SelectItem key={slot24} value={slot12}>
                              {slot12}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-9 w-full bg-card">
                    <SelectValue>
                      <div className="flex items-center gap-2 font-medium">
                        <span className={statusDotClass} />
                        <span className="capitalize">{formatStatusLabel(status)}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent position="popper" className="w-[var(--radix-select-trigger-width)]">
                    <SelectItem value="confirmed">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 shrink-0 rounded-full bg-green-500" />
                        <span>Confirmed</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="completed">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40" />
                        <span>Completed</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="cancelled">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
                        <span>Cancelled</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Customer</Label>
                <div className="flex w-full items-center justify-between gap-4 rounded-md border border-input bg-card px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium leading-tight">{displayCustomerName}</div>
                    <div className="truncate text-xs text-muted-foreground">{displayCustomerEmail}</div>
                  </div>
                  <button
                    type="button"
                    onClick={handleEditCustomer}
                    className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                    title="Edit customer details"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Price</Label>
                <div className="flex h-9 w-full items-center rounded-md border border-input bg-card px-3 text-sm font-medium tabular-nums">
                  ${price.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="items-center sm:justify-between">
            {readOnly ? (
              <>
                <div />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="standard-action-button admin-modal-secondary-button neutral-action-button"
                  onClick={onClose}
                >
                  Close
                </Button>
              </>
            ) : (
              <>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="ghost" size="sm" className="text-destructive hover:bg-muted/60 hover:text-destructive">
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete booking?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete the booking.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="standard-action-button admin-modal-secondary-button neutral-action-button"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" className="standard-action-button" onClick={handleSave} disabled={loading}>
                    {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {loading ? "Saving..." : "Save changes"}
                  </Button>
                </div>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerEditModal
        customer={customerData}
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        onSaveSuccess={async (updatedCustomer) => {
          if (!booking) return;

          // Update the bookings table with the new customer name/email
          const { error } = await supabase
            .from("bookings")
            .update({
              customer_name: updatedCustomer.name,
              customer_email: updatedCustomer.email,
            })
            .eq("booking_id", booking.booking_id);

          if (error) {
            toast.error("Customer updated but failed to sync to booking record");
          } else {
            // Update local display state so modal shows the new name immediately
            setDisplayCustomerName(updatedCustomer.name);
            setDisplayCustomerEmail(updatedCustomer.email);
            toast.success("Customer updated successfully");
            // Refresh parent booking list so the table row also updates
            onSaveSuccess();
          }
        }}
      />
    </>
  );
};

export default BookingDetailsModal;
